/**
 * Utilidades para formatear fechas en zona horaria de República Dominicana
 * Zona horaria: America/Santo_Domingo (UTC-4)
 */

/**
 * Formatea una fecha en zona horaria de República Dominicana
 * @param {Date|string} date - Fecha a formatear (puede venir en UTC desde MongoDB)
 * @param {Object} options - Opciones de formato (year, month, day, etc.)
 * @returns {string} Fecha formateada
 */
export function formatDateRD(date, options = {}) {
  if (!date) return 'Sin fecha';
  
  const defaultOptions = {
    timeZone: 'America/Santo_Domingo',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };
  
  try {
    let dateObj;
    
    // Si es string, verificar si es solo fecha (YYYY-MM-DD) o incluye hora
    if (typeof date === 'string') {
      // Si es formato YYYY-MM-DD (solo fecha, sin hora), crear fecha local en RD
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        // Es solo fecha, crear como fecha local en RD (no UTC)
        const [year, month, day] = date.split('-').map(Number);
        dateObj = new Date(year, month - 1, day);
      } else if (date.includes('T') && date.endsWith('Z')) {
        // Es formato ISO con Z (UTC), convertir a RD
        dateObj = new Date(date);
      } else {
        // Otro formato, intentar parsear normalmente
        dateObj = new Date(date);
      }
    } else {
      // Ya es Date object
      dateObj = date instanceof Date ? date : new Date(date);
    }
    
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    // Si la fecha original era solo YYYY-MM-DD (sin hora), formatear directamente
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      const dateOnly = new Date(year, month - 1, day);
      return dateOnly.toLocaleDateString('es-DO', defaultOptions);
    }
    
    // Para fechas con hora, convertir a zona horaria de RD
    return dateObj.toLocaleDateString('es-DO', defaultOptions);
  } catch (error) {
    console.error('Error formateando fecha:', error);
    return 'Fecha inválida';
  }
}

/**
 * Formatea fecha y hora en zona horaria de República Dominicana
 * @param {Date|string} date - Fecha a formatear (puede venir en UTC desde MongoDB)
 * @param {Object} options - Opciones de formato
 * @returns {string} Fecha y hora formateada
 */
export function formatDateTimeRD(date, options = {}) {
  if (!date) return 'Sin fecha';
  
  const defaultOptions = {
    timeZone: 'America/Santo_Domingo',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Usar formato 24 horas por defecto
    ...options
  };
  
  try {
    // Si es string, crear Date object (si viene de MongoDB en formato ISO, se parsea correctamente)
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      console.error('Fecha inválida:', date);
      return 'Fecha inválida';
    }
    
    // Debug: mostrar fecha original y convertida
    if (process.env.NODE_ENV === 'development') {
      console.log('Formateando fecha:', {
        original: date,
        dateObj: dateObj.toISOString(),
        utc: dateObj.toUTCString(),
        rd: dateObj.toLocaleString('es-DO', { timeZone: 'America/Santo_Domingo' })
      });
    }
    
    // Usar toLocaleString con timeZone para convertir correctamente de UTC a RD
    const formatted = dateObj.toLocaleString('es-DO', defaultOptions);
    
    // Si hour12 es false pero el formato incluye AM/PM, convertir manualmente
    if (defaultOptions.hour12 === false && formatted.includes('AM')) {
      // Ya está en formato 24h, no hacer nada
      return formatted;
    }
    
    return formatted;
  } catch (error) {
    console.error('Error formateando fecha y hora:', error, 'Fecha original:', date);
    return 'Fecha inválida';
  }
}

/**
 * Formatea solo la fecha en formato corto (DD/MM/YYYY)
 * @param {Date|string} date - Fecha a formatear (puede venir en UTC desde MongoDB)
 * @returns {string} Fecha formateada
 */
export function formatDateShortRD(date) {
  if (!date) return 'Sin fecha';
  
  try {
    let dateObj;
    
    // Si es string, verificar si es solo fecha (YYYY-MM-DD) o incluye hora
    if (typeof date === 'string') {
      // Si es formato YYYY-MM-DD (solo fecha, sin hora), crear fecha local en RD
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        // Es solo fecha, crear como fecha local en RD (no UTC)
        const [year, month, day] = date.split('-').map(Number);
        // Crear fecha en zona horaria local (será interpretada como RD)
        dateObj = new Date(year, month - 1, day);
      } else if (date.includes('T') && date.endsWith('Z')) {
        // Es formato ISO con Z (UTC), convertir a RD
        dateObj = new Date(date);
      } else {
        // Otro formato, intentar parsear normalmente
        dateObj = new Date(date);
      }
    } else {
      // Ya es Date object
      dateObj = date instanceof Date ? date : new Date(date);
    }
    
    if (isNaN(dateObj.getTime())) {
      console.error('Fecha inválida en formatDateShortRD:', date);
      return 'Fecha inválida';
    }
    
    // Si la fecha original era solo YYYY-MM-DD (sin hora), usar componentes directamente
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Para fechas con hora, convertir a zona horaria de RD
    return dateObj.toLocaleDateString('es-DO', {
      timeZone: 'America/Santo_Domingo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    console.error('Error formateando fecha corta:', error, 'Fecha original:', date);
    return 'Fecha inválida';
  }
}

/**
 * Obtiene la fecha actual en zona horaria de República Dominicana
 * @returns {Date} Fecha actual en RD
 */
export function getCurrentDateRD() {
  const now = new Date();
  // Convertir a string en zona horaria de RD y luego crear nueva fecha
  const rdString = now.toLocaleString('en-US', { timeZone: 'America/Santo_Domingo' });
  return new Date(rdString);
}

/**
 * Convierte una fecha a formato datetime-local para input HTML
 * en zona horaria de República Dominicana
 * @param {Date|string} date - Fecha a convertir
 * @returns {string} Fecha en formato YYYY-MM-DDTHH:mm
 */
export function toDateTimeLocalRD(date) {
  if (!date) return '';
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    
    // Obtener componentes en zona horaria de RD usando Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Santo_Domingo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(dateObj);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const hour = parts.find(p => p.type === 'hour').value;
    const minute = parts.find(p => p.type === 'minute').value;
    
    return `${year}-${month}-${day}T${hour}:${minute}`;
  } catch (error) {
    console.error('Error convirtiendo fecha a datetime-local:', error);
    // Fallback: usar fecha local si falla
    const dateObj = date instanceof Date ? date : new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
}

